import {
  Briefcase,
  Building,
  Building2,
  Calendar,
  Copy,
  ExternalLink,
  Globe,
  Hash,
  LayoutGrid,
  Mail,
  MapPin,
  Phone,
  Star,
  Target,
  TrendingUp,
  User,
  UserCircle,
  Users
} from 'lucide-react';

import { SearchResult } from '../../hooks/useCommandPalette';
import { RecentItem } from '../../hooks/useRecentItems';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';

type Entity = RecentItem | SearchResult;

interface ItemPreviewProps {
  item: Entity | null;
  onAction: (action: string) => void;
}

const getEntityIcon = (type: string) => {
  switch (type) {
    case 'account':
      return Building2;
    case 'contact':
      return User;
    case 'opportunity':
      return Target;
    case 'view':
      return LayoutGrid;
    default:
      return User;
  }
};

const getEntityLabel = (type: string) => {
  switch (type) {
    case 'account':
      return 'Account';
    case 'contact':
      return 'Contact';
    case 'opportunity':
      return 'Investment Opportunity';
    case 'view':
      return 'Saved View';
    default:
      return '';
  }
};

const getStatusColor = (status?: string) => {
  if (!status) return 'default';
  const lower = status.toLowerCase();
  if (lower.includes('active')) return 'default';
  if (lower.includes('killed') || lower.includes('closed'))
    return 'destructive';
  if (lower.includes('screening') || lower.includes('staffing'))
    return 'secondary';
  return 'outline';
};

export function ItemPreview({ item, onAction }: ItemPreviewProps) {
  if (!item) {
    return (
      <div className="bg-muted/30 flex flex-1 items-center justify-center border-l p-6">
        <div className="text-muted-foreground text-center">
          <p className="text-sm">Select an item to view details</p>
        </div>
      </div>
    );
  }

  const Icon = getEntityIcon(item.type);

  return (
    <div className="bg-muted/30 flex flex-1 flex-col gap-5 overflow-auto border-l p-6">
      {/* Header */}
      <div className="space-y-3">
        <div className="flex items-start gap-3">
          <div className="bg-primary/10 border-primary/20 rounded-lg border p-2">
            <Icon className="text-primary h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-muted-foreground mb-1 text-xs">
              {getEntityLabel(item.type)}
            </p>
            <h3 className="text-base leading-tight font-semibold">
              {item.name}
            </h3>
          </div>
        </div>
      </div>

      <Separator />

      {/* Details Section */}
      <div className="space-y-4">
        {item.type === 'account' && (
          <>
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <MapPin className="text-muted-foreground h-3.5 w-3.5" />
                <span className="text-muted-foreground text-xs font-medium">
                  Location
                </span>
              </div>
              <p className="text-sm">
                {item.metadata?.city || 'Not specified'}
              </p>
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <TrendingUp className="text-muted-foreground h-3.5 w-3.5" />
                <span className="text-muted-foreground text-xs font-medium">
                  Investor Type
                </span>
              </div>
              {item.metadata?.investorCategory ? (
                <Badge
                  className="font-normal"
                  variant="secondary"
                >
                  {item.metadata.investorCategory}
                </Badge>
              ) : (
                <p className="text-muted-foreground text-sm">Not specified</p>
              )}
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <Briefcase className="text-muted-foreground h-3.5 w-3.5" />
                <span className="text-muted-foreground text-xs font-medium">
                  Industry
                </span>
              </div>
              <p className="text-sm">
                {item.metadata?.industryName || 'Not specified'}
              </p>
            </div>

            {item.metadata?.phone && (
              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <Phone className="text-muted-foreground h-3.5 w-3.5" />
                  <span className="text-muted-foreground text-xs font-medium">
                    Phone
                  </span>
                </div>
                <p className="text-muted-foreground text-sm">
                  {item.metadata.phone}
                </p>
              </div>
            )}

            {item.metadata?.email && (
              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <Mail className="text-muted-foreground h-3.5 w-3.5" />
                  <span className="text-muted-foreground text-xs font-medium">
                    Email
                  </span>
                </div>
                <p className="text-muted-foreground truncate text-sm">
                  {item.metadata.email}
                </p>
              </div>
            )}
          </>
        )}

        {item.type === 'contact' && (
          <>
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <Briefcase className="text-muted-foreground h-3.5 w-3.5" />
                <span className="text-muted-foreground text-xs font-medium">
                  Position
                </span>
              </div>
              <p className="text-sm font-medium">
                {item.metadata?.function || 'Not specified'}
              </p>
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <Building className="text-muted-foreground h-3.5 w-3.5" />
                <span className="text-muted-foreground text-xs font-medium">
                  Company
                </span>
              </div>
              <p className="text-sm">
                {item.metadata?.companyName || 'Not specified'}
              </p>
            </div>

            {item.metadata?.city && (
              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <MapPin className="text-muted-foreground h-3.5 w-3.5" />
                  <span className="text-muted-foreground text-xs font-medium">
                    Location
                  </span>
                </div>
                <p className="text-sm">{item.metadata.city}</p>
              </div>
            )}

            {item.metadata?.email && (
              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <Mail className="text-muted-foreground h-3.5 w-3.5" />
                  <span className="text-muted-foreground text-xs font-medium">
                    Email
                  </span>
                </div>
                <p className="text-muted-foreground truncate text-sm">
                  {item.metadata.email}
                </p>
              </div>
            )}

            {(item.metadata?.mobile || item.metadata?.phone) && (
              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <Phone className="text-muted-foreground h-3.5 w-3.5" />
                  <span className="text-muted-foreground text-xs font-medium">
                    Phone
                  </span>
                </div>
                <p className="text-muted-foreground text-sm">
                  {item.metadata.mobile || item.metadata.phone}
                </p>
              </div>
            )}
          </>
        )}

        {item.type === 'opportunity' && (
          <>
            {item.metadata?.projectName && (
              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <Briefcase className="text-muted-foreground h-3.5 w-3.5" />
                  <span className="text-muted-foreground text-xs font-medium">
                    Project Name
                  </span>
                </div>
                <p className="text-sm font-medium">
                  {item.metadata.projectName}
                </p>
              </div>
            )}

            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <Target className="text-muted-foreground h-3.5 w-3.5" />
                <span className="text-muted-foreground text-xs font-medium">
                  Status
                </span>
              </div>
              {item.metadata?.status ? (
                <Badge
                  className="font-normal"
                  variant={getStatusColor(item.metadata.status)}
                >
                  {item.metadata.status}
                </Badge>
              ) : (
                <p className="text-muted-foreground text-sm">Not specified</p>
              )}
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <TrendingUp className="text-muted-foreground h-3.5 w-3.5" />
                <span className="text-muted-foreground text-xs font-medium">
                  Stage
                </span>
              </div>
              <p className="text-sm font-medium">
                {item.metadata?.stage || 'Not specified'}
              </p>
            </div>

            {item.metadata?.pipeline && (
              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <Globe className="text-muted-foreground h-3.5 w-3.5" />
                  <span className="text-muted-foreground text-xs font-medium">
                    Pipeline
                  </span>
                </div>
                <p className="text-sm capitalize">
                  {item.metadata.pipeline.replace(/_/g, ' ')}
                </p>
              </div>
            )}

            {item.metadata?.closingDate && (
              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <Calendar className="text-muted-foreground h-3.5 w-3.5" />
                  <span className="text-muted-foreground text-xs font-medium">
                    Target Close
                  </span>
                </div>
                <p className="text-sm">
                  {new Date(item.metadata.closingDate).toLocaleDateString()}
                </p>
              </div>
            )}
          </>
        )}

        {item.type === 'view' && (
          <>
            {item.metadata?.description && (
              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <Briefcase className="text-muted-foreground h-3.5 w-3.5" />
                  <span className="text-muted-foreground text-xs font-medium">
                    Description
                  </span>
                </div>
                <p className="text-sm">{item.metadata.description}</p>
              </div>
            )}

            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <Target className="text-muted-foreground h-3.5 w-3.5" />
                <span className="text-muted-foreground text-xs font-medium">
                  View Type
                </span>
              </div>
              <Badge
                className="font-normal"
                variant="secondary"
              >
                {item.metadata?.targetModel === 'account'
                  ? 'Accounts'
                  : 'Contacts'}
              </Badge>
            </div>

            {item.metadata?.category && (
              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <LayoutGrid className="text-muted-foreground h-3.5 w-3.5" />
                  <span className="text-muted-foreground text-xs font-medium">
                    Category
                  </span>
                </div>
                <p className="text-sm">{item.metadata.category}</p>
              </div>
            )}

            {item.metadata?.recordCount && (
              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <Hash className="text-muted-foreground h-3.5 w-3.5" />
                  <span className="text-muted-foreground text-xs font-medium">
                    Records
                  </span>
                </div>
                <Badge
                  className="font-normal"
                  variant="outline"
                >
                  {item.metadata.recordCount} records
                </Badge>
              </div>
            )}

            {item.metadata?.isFavorite === 'true' && (
              <div className="flex items-center gap-2 text-sm text-yellow-600 dark:text-yellow-500">
                <Star className="h-4 w-4 fill-current" />
                <span className="font-medium">Favorite</span>
              </div>
            )}
          </>
        )}
      </div>

      <Separator />

      {/* Actions */}
      <div className="mt-auto space-y-2">
        <h4 className="text-muted-foreground mb-3 text-xs font-medium tracking-wide uppercase">
          Quick Actions
        </h4>

        <button
          className="hover:bg-background hover:border-border flex w-full items-center justify-between rounded-lg border border-transparent px-3 py-2.5 text-sm font-medium transition-all"
          onClick={() => {
            onAction('open');
          }}
        >
          <div className="flex items-center gap-2">
            <ExternalLink className="h-4 w-4" />
            <span>Open</span>
          </div>
          <kbd className="bg-background text-muted-foreground pointer-events-none inline-flex h-5 items-center gap-1 rounded border px-1.5 font-mono text-[10px] font-medium select-none">
            ↵
          </kbd>
        </button>

        <button
          className="hover:bg-background hover:border-border flex w-full items-center justify-between rounded-lg border border-transparent px-3 py-2.5 text-sm transition-all"
          onClick={() => {
            onAction('open-new-tab');
          }}
        >
          <div className="flex items-center gap-2">
            <ExternalLink className="text-muted-foreground h-4 w-4" />
            <span>Open in new tab</span>
          </div>
          <kbd className="bg-background text-muted-foreground pointer-events-none inline-flex h-5 items-center gap-1 rounded border px-1.5 font-mono text-[10px] font-medium select-none">
            <span className="text-xs">⌘⇧</span>↵
          </kbd>
        </button>

        {item.type === 'contact' && item.metadata?.email && (
          <button
            className="hover:bg-background hover:border-border flex w-full items-center justify-between rounded-lg border border-transparent px-3 py-2.5 text-sm transition-all"
            onClick={() => {
              onAction('email');
            }}
          >
            <div className="flex items-center gap-2">
              <Mail className="text-muted-foreground h-4 w-4" />
              <span>Send email</span>
            </div>
            <kbd className="bg-background text-muted-foreground pointer-events-none inline-flex h-5 items-center gap-1 rounded border px-1.5 font-mono text-[10px] font-medium select-none">
              <span className="text-xs">⌘</span>E
            </kbd>
          </button>
        )}

        {item.metadata?.email && (
          <button
            className="hover:bg-background hover:border-border flex w-full items-center justify-between rounded-lg border border-transparent px-3 py-2.5 text-sm transition-all"
            onClick={() => {
              onAction('copy-email');
            }}
          >
            <div className="flex items-center gap-2">
              <Copy className="text-muted-foreground h-4 w-4" />
              <span>Copy email</span>
            </div>
            <kbd className="bg-background text-muted-foreground pointer-events-none inline-flex h-5 items-center gap-1 rounded border px-1.5 font-mono text-[10px] font-medium select-none">
              <span className="text-xs">⌘</span>C
            </kbd>
          </button>
        )}
      </div>
    </div>
  );
}
